import React, { useEffect } from 'react';
import { usePageSeo } from '../hooks/usePageSeo';
import { Button } from '../components/ui/button';
import { ArrowLeft, ChevronRight } from 'lucide-react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { articles } from '../data/articles';
import NotFound from './NotFound';
import Header from '../components/Header';

function formatDate(iso) {
  return new Date(iso + 'T00:00:00').toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
}

const ArticleDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const article = articles.find((a) => a.slug === slug);

  usePageSeo({
    title: article ? `${article.title} | Civic Rentals LLC` : 'Page Not Found | Civic Rentals LLC',
    description: article?.summary || '',
    canonical: article ? `https://civicrentals.com/articles/${article.slug}` : undefined,
    ogTitle: article ? `${article.title} | Civic Rentals LLC` : undefined,
    ogDescription: article?.summary || '',
    ogUrl: article ? `https://civicrentals.com/articles/${article.slug}` : undefined,
    ogType: 'article',
    ogImage: 'https://civicrentals.com/favicon.png',
    twitterCard: 'summary',
    noindex: !article,
  });

  // Inject Article + BreadcrumbList schema
  useEffect(() => {
    if (!article) return;
    const schema = {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'Article',
          headline: article.title,
          description: article.summary,
          datePublished: article.publishedDate,
          dateModified: article.updatedDate,
          author: {
            '@type': 'Person',
            name: article.author,
            url: article.authorUrl,
          },
          publisher: {
            '@type': 'Organization',
            name: 'Civic Rentals LLC',
            url: 'https://civicrentals.com',
            logo: { '@type': 'ImageObject', url: 'https://civicrentals.com/logo.png' },
          },
          mainEntityOfPage: `https://civicrentals.com/articles/${article.slug}`,
        },
        {
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://civicrentals.com' },
            { '@type': 'ListItem', position: 2, name: 'Articles', item: 'https://civicrentals.com/articles' },
            { '@type': 'ListItem', position: 3, name: article.title, item: `https://civicrentals.com/articles/${article.slug}` },
          ],
        },
      ],
    };
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-article-schema', 'true');
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);
    return () => { script.remove(); };
  }, [article]);

  if (!article) return <NotFound />;

  // Find related articles (exclude current)
  const related = articles.filter(a => a.slug !== article.slug).slice(0, 2);

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#d5e0de' }}>

      <Header active="Articles" />

      <section className="pt-28 pb-20 px-6">
        <div className="container mx-auto max-w-3xl">
          {/* Breadcrumbs */}
          <nav aria-label="Breadcrumb" className="mb-6" data-testid="breadcrumbs">
            <ol className="flex items-center gap-1.5 text-sm text-slate-500">
              <li><Link to="/" className="hover:text-slate-800 transition-colors">Home</Link></li>
              <li><ChevronRight className="h-3.5 w-3.5" /></li>
              <li><Link to="/articles" className="hover:text-slate-800 transition-colors">Articles</Link></li>
              <li><ChevronRight className="h-3.5 w-3.5" /></li>
              <li className="text-slate-700 font-medium truncate max-w-[200px]">{article.title}</li>
            </ol>
          </nav>

          <article className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
            <div className="px-8 py-8 sm:px-10 sm:py-10">
              {/* Date + Author */}
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-slate-500 mb-3">
                <time dateTime={article.publishedDate}>Published {formatDate(article.publishedDate)}</time>
                {article.updatedDate && article.updatedDate !== article.publishedDate && (
                  <time dateTime={article.updatedDate}>Updated {formatDate(article.updatedDate)}</time>
                )}
              </div>
              <p className="text-sm text-slate-600 mb-5">
                Written by{' '}
                <a href={article.authorUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 font-medium">
                  {article.author}
                </a>
              </p>

              <h1 className="text-3xl font-bold text-slate-900 mb-8">{article.title}</h1>

              {article.content.map((p, i) => (
                <p key={i} className="text-slate-700 leading-relaxed mb-4">{p}</p>
              ))}

              {article.sections.map((section, si) => (
                <div key={si} className="mt-6">
                  {section.heading && (
                    <h2 className="text-lg font-semibold text-slate-800 mb-3">{section.heading}</h2>
                  )}
                  {section.paragraphs?.map((p, pi) => (
                    <p key={pi} className="text-slate-700 leading-relaxed mb-4">{p}</p>
                  ))}
                  {section.list && (
                    <ul className="list-disc list-inside space-y-2 mb-4 ml-2">
                      {section.list.map((item, li) => (
                        <li key={li} className="text-slate-700 leading-relaxed">{item}</li>
                      ))}
                    </ul>
                  )}
                  {section.after?.map((p, ai) => (
                    <p key={ai} className="text-slate-700 leading-relaxed mb-4">{p}</p>
                  ))}
                </div>
              ))}
            </div>
          </article>

          {/* Related Articles */}
          {related.length > 0 && (
            <div className="mt-10">
              <h3 className="text-lg font-semibold text-slate-800 mb-4">More from Civic Rentals LLC</h3>
              <div className="grid gap-4">
                {related.map(r => (
                  <Link key={r.slug} to={`/articles/${r.slug}`} className="bg-white rounded-lg border border-slate-200 p-5 hover:shadow-md transition-shadow block no-underline">
                    <p className="text-sm text-slate-500 mb-1">{r.date}</p>
                    <p className="text-slate-900 font-semibold hover:text-blue-600 transition-colors">{r.title}</p>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      <footer className="bg-slate-900 text-slate-400 py-12 px-6 border-t border-slate-800">
        <div className="container mx-auto max-w-6xl">
          <div className="border-t border-slate-800 pt-8 text-center text-sm">
            <p>&copy; 2026 Civic Rentals LLC. All rights reserved.</p>
            <p className="mt-2 space-x-4">
              <a href="/privacypolicy" className="hover:text-white transition-colors">Privacy Policy</a>
              <a href="/websiteTOS" className="hover:text-white transition-colors">Terms of Service</a>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ArticleDetail;
